import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'
import { useAuthStore } from '@/store/authStore'
import { useProfileStore } from '@/store/profileStore'
import { setContext as setSentryContext, addBreadcrumb } from '@/config/sentry'
import { SELECTED_ORGANIZATION_ID_KEY } from '@/lib/sessionStorageKeys'

/**
 * Organization interface matching the GraphQL OrganizationType schema.
 * The organizations query returns only companies the logged-in user has
 * access to, resolved via ProfileCompany / ProfileEntity / ProfileProvider.
 */
export interface Organization {
  id: string
  name: string
  legalName: string | null
  description: string | null
  companyType: string | null
  status: string | null
  activeProvidersCount: number
  createdAt: string
  displayName: string
  /** Entity UUID for provider registration `entity_id` (first entity under this company). */
  registrationDefaultEntityId: string | null
}

interface OrganizationContextType {
  currentOrganization: Organization | null
  organizations: Organization[]
  setCurrentOrganization: (org: Organization) => void
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

/**
 * GraphQL query for fetching organizations scoped to the logged-in user.
 * Backend resolves via resolve_organization_context():
 *   Staff/Admin/Operation → all companies
 *   ProfileCompany       → Company directly
 *   ProfileEntity        → Entity → Company
 *   ProfileProvider      → Entity → Company
 */
const GET_ORGANIZATIONS_QUERY = `
  query GetUserOrganizations {
    organizations {
      id
      name
      legalName
      description
      companyType
      status
      activeProvidersCount
      createdAt
      entities {
        id
        createdAt
      }
    }
  }
`

interface OrganizationsResponse {
  organizations: Array<{
    id: string
    name: string
    legalName: string | null
    description: string | null
    companyType: string | null
    status: string | null
    activeProvidersCount: number
    createdAt: string
    entities?: Array<{ id: string; createdAt?: string | null }> | null
  }>
}

/**
 * Same ordering as backend `resolve_registration_default_entity_id`:
 * first Entity under the company by `created_at`, then `id`.
 */
function computeRegistrationDefaultEntityId(
  entities: Array<{ id: string; createdAt?: string | null }> | null | undefined,
): string | null {
  if (!entities?.length) return null
  const sorted = [...entities].sort((a, b) => {
    const ta =
      a.createdAt != null && a.createdAt !== ''
        ? Date.parse(a.createdAt)
        : Number.NaN
    const tb =
      b.createdAt != null && b.createdAt !== ''
        ? Date.parse(b.createdAt)
        : Number.NaN
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) {
      return ta - tb
    }
    return String(a.id).localeCompare(String(b.id))
  })
  return sorted[0]?.id ?? null
}

function transformOrganizations(data: OrganizationsResponse): Organization[] {
  if (!data?.organizations) return []

  return data.organizations.map((org) => {
    const { entities: _entities, ...rest } = org
    return {
      ...rest,
      registrationDefaultEntityId: computeRegistrationDefaultEntityId(_entities),
      displayName: org.legalName || org.name || `Organization ${org.id}`,
    }
  })
}

function useOrganizationsQuery() {
  const { isAuthenticated, accessToken } = useAuthStore()
  const profileMode = useProfileStore((s) => s.activeMode)
  
  return useQuery({
    queryKey: ['organizations', profileMode, accessToken ?? ''],
    queryFn: async () => {
      const response = await graphqlQuery<OrganizationsResponse>(
        GET_ORGANIZATIONS_QUERY
      )
      
      if (!response.data?.organizations) {
        throw new Error('Failed to fetch organizations')
      }
      
      return transformOrganizations(response.data)
    },
    enabled: isAuthenticated && profileMode !== 'business_client',
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { data: organizations = [], isLoading, isError, error, refetch } = useOrganizationsQuery()
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const authUserId = useAuthStore((s) => s.user?.id)
  const prevAuthUserIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentOrganizationState(null)
      localStorage.removeItem(SELECTED_ORGANIZATION_ID_KEY)
      prevAuthUserIdRef.current = undefined
      return
    }

    if (
      authUserId &&
      prevAuthUserIdRef.current !== undefined &&
      prevAuthUserIdRef.current !== authUserId
    ) {
      setCurrentOrganizationState(null)
      localStorage.removeItem(SELECTED_ORGANIZATION_ID_KEY)
    }

    if (authUserId) {
      prevAuthUserIdRef.current = authUserId
    }
  }, [isAuthenticated, authUserId])

  // Set initial organization when data loads
  useEffect(() => {
    if (organizations.length > 0 && !currentOrganization) {
      // Try to restore previously selected organization
      const savedOrgId = localStorage.getItem(SELECTED_ORGANIZATION_ID_KEY)
      const savedOrg = savedOrgId 
        ? organizations.find(org => org.id === savedOrgId)
        : null
      
      // Use saved organization or first in list
      setCurrentOrganizationState(savedOrg || organizations[0])
    }
  }, [organizations, currentOrganization])
  
  // Wrapper to persist selection + update analytics
  const setCurrentOrganization = (org: Organization) => {
    setCurrentOrganizationState(org)
    localStorage.setItem(SELECTED_ORGANIZATION_ID_KEY, org.id)

    // Sentry: update organization context
    setSentryContext('organization', { id: org.id, name: org.displayName })
    addBreadcrumb({
      category: 'organization',
      message: `Organization changed to ${org.displayName}`,
      level: 'info',
      data: { organization_id: org.id, organization_name: org.displayName },
    })
  }

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        setCurrentOrganization,
        isLoading,
        isError,
        error: error as Error | null,
        refetch,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}
